import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router';
import { Plus, Download, Loader, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { identificationLogApi } from '@/api/production/logs';
import { IdentificationLogTable } from '@/components/tables/production/identification-log-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeInput } from '@/components/ui/date-range-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { useDebounce } from '@/hooks/use-debounce';

export function IdentificationLogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from')!) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to')!) : undefined,
  });
  const from = dateRange.from?.toISOString();
  const to = dateRange.to?.toISOString();
  const hasDateFilter = !!(from || to);

  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['identification-logs', 'stats'],
    queryFn: () => identificationLogApi.getStats(),
  });

  // Fetch logs with debounced search
  const { data: logs, isLoading } = useQuery({
    queryKey: ['identification-logs', { query: debouncedQuery, from, to, page }],
    queryFn: () =>
      identificationLogApi.findMany({
        query: debouncedQuery || undefined,
        from,
        to,
        page,
        limit,
      }),
  });

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('query', value);
    } else {
      newParams.delete('query');
    }
    newParams.set('page', '1'); // Reset to first page on search
    setSearchParams(newParams);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    const newParams = new URLSearchParams(searchParams);
    if (range.from) newParams.set('from', range.from.toISOString());
    else newParams.delete('from');
    if (range.to) newParams.set('to', range.to.toISOString());
    else newParams.delete('to');
    newParams.set('page', '1'); // Reset to first page on filter change
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const exportMutation = useMutation({
    mutationKey: ['export-identification-logs'],
    mutationFn: async () => {
      const blob = await identificationLogApi.export({
        query: debouncedQuery || undefined,
        from,
        to,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export.identification-log.${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });

  const handleExport = () => {
    exportMutation.mutate();
  };

  return (
    <div>
      {/* Header */}
      <div className="border-b border-gray-900/10 pb-6 flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">
            Log de identificação
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Uma lista de todos os registros de identificação.
          </p>
        </div>

        <Link
          to="/production/tool/identificator"
          className="ml-auto flex items-center gap-x-1 rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
          title="Faça uma nova identificação"
        >
          <Plus className="-ml-1.5 h-5 w-5" aria-hidden="true" />
          Nova identificação
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="mt-6 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sucesso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {stats.success}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Falha</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {stats.failed}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Search Form */}
      <div className="border-b border-gray-900/10 pb-6 mt-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="search">Pesquisar</Label>
              <Button
                variant="outline"
                size="sm"
                disabled={exportMutation.isPending}
                type="button"
                title="Exporte os dados filtrados"
                onClick={handleExport}
              >
                {exportMutation.isPending ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </>
                )}
              </Button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                id="search"
                type="text"
                placeholder="Serial, IMEI, ICCID, Firmware, Tecnologia, Usuário..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="flex-1"
              />

              <div className="flex items-center gap-2">
                <div className="w-full sm:w-80" title="Filtrar por data e hora">
                  <DateRangeInput
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    showTimeInputs
                  />
                </div>

                {hasDateFilter && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    title="Limpar filtro de data"
                    onClick={() => handleDateRangeChange({ from: undefined, to: undefined })}
                    className="!h-10 !w-10 !min-w-10 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="w-full text-center py-8">
            <p className="text-gray-500">Carregando...</p>
          </div>
        ) : (
          <>
            <IdentificationLogTable data={logs?.docs || []} />

            {(logs?.pages ?? 1) > 1 && (
              <div className="mt-4 overflow-hidden rounded-md border">
                <Pagination
                  currentPage={page}
                  totalPages={logs?.pages ?? 1}
                  totalItems={logs?.total ?? 0}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
